import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Popconfirm, Result, Spin } from "antd";
import { cancelAppointment, getAppointment, getAppointmentSlots, rescheduleAppointment } from "./calendarService";
import WeekSlots from "./WeekSlots";
import styles from "./style.module.scss";

function whenLabel(iso, tz, locale) {
  return new Intl.DateTimeFormat(locale, { timeZone: tz, weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

/** The customer's private link: app.pivony.com/appointment/<token>. Cancel or pick another time. */
const PublicAppointment = () => {
  const { token } = useParams();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [booking, setBooking] = useState(null);
  const [missing, setMissing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setBooking(null);
    getAppointment(token)
      .then((res) => {
        setBooking(res.booking);
        if (res.booking?.locale && res.booking.locale !== i18n.language) i18n.changeLanguage(res.booking.locale);
      })
      .catch(() => setMissing(true));
  }, [token, i18n]);

  const load = useCallback(({ from, days }) => getAppointmentSlots(token, { from, days }), [token]);
  const locale = i18n.language || "en";

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await cancelAppointment(token);
      setBooking(res.booking);
      setNotice(t("sonic_calendar.public_cancelled"));
    } catch (_) {
      setError(t("sonic_calendar.public_error"));
    } finally {
      setBusy(false);
    }
  };

  const move = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await rescheduleAppointment(token, picked);
      setMoving(false);
      setPicked(null);
      setNotice(t("sonic_calendar.public_moved"));
      history.replace(`/appointment/${res.manage_token}`);
    } catch (err) {
      if (err.code === "slot_taken") {
        setError(t("sonic_calendar.public_slot_taken"));
        setPicked(null);
        setReloadKey((k) => k + 1);
      } else {
        setError(t("sonic_calendar.public_error"));
      }
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return (
      <div className={styles.publicPage}>
        <Result status="404" title={t("sonic_calendar.public_link_invalid")} />
      </div>
    );
  }
  if (!booking) {
    return (
      <div className={styles.publicPage}>
        <Spin />
      </div>
    );
  }
  const cancelled = booking.status === "cancelled";

  return (
    <div className={styles.publicPage}>
      <main className={styles.publicCard}>
        <h1>{booking.business_name}</h1>
        <p className={styles.publicSub}>{t("sonic_calendar.public_your_appointment")}</p>
        {notice ? (
          <p className={styles.done} role="status">
            {notice}
          </p>
        ) : null}
        <section className={styles.publicStep}>
          <h2>{booking.service_name}</h2>
          <p>
            {whenLabel(booking.start_at, booking.timezone, locale)} ({booking.timezone.replace(/_/g, " ")})
            {booking.location ? ` · ${booking.location}` : ""}
          </p>
          {booking.customer_name ? <p className={styles.serviceMeta}>{booking.customer_name}</p> : null}
          {cancelled ? <p className={styles.error}>{t("sonic_calendar.public_status_cancelled")}</p> : null}
        </section>
        {!cancelled && !moving ? (
          <section className={styles.publicStep}>
            <Button onClick={() => setMoving(true)} style={{ marginRight: 8 }}>
              {t("sonic_calendar.public_pick_other")}
            </Button>
            <Popconfirm title={t("sonic_calendar.public_cancel_confirm")} onConfirm={cancel}>
              <Button danger loading={busy}>
                {t("sonic_calendar.public_cancel")}
              </Button>
            </Popconfirm>
          </section>
        ) : null}
        {moving ? (
          <section className={styles.publicStep}>
            <h2>{t("sonic_calendar.public_pick_time")}</h2>
            <WeekSlots load={load} tz={booking.timezone} selected={picked} onPick={setPicked} t={t} locale={locale} reloadKey={reloadKey} />
            {error ? <p className={styles.error}>{error}</p> : null}
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <Button type="primary" disabled={!picked} loading={busy} onClick={move}>
                {t("sonic_calendar.public_move_confirm")}
              </Button>
              <Button onClick={() => setMoving(false)}>{t("sonic_calendar.public_back")}</Button>
            </div>
          </section>
        ) : null}
        <p className={styles.footer}>{t("sonic_calendar.public_footer")}</p>
      </main>
    </div>
  );
};

export default PublicAppointment;
